import { Random } from './Random';
import { vec2 } from './Vec2';
import type { Vec2 } from './Vec2';
import type { Segment } from './Terrain';
import { SegmentTerrain } from './Terrain';
import type { Terrain, CollisionResult } from './Physics';
import { createBall, step, PHYSICS, wrapX } from './Physics';
import { isInHole } from './HoleDetection';
import { GAME_WIDTH, GAME_HEIGHT } from '../constants';
import { TEE_HEIGHT, HOLE_WIDTH } from './GameObjects';
import { lengthSq } from './Vec2';
import { connectSegments, connectSegmentsDebug, subdivideConnections } from './TerrainConnector';

export interface LevelData {
  segments: Segment[];
  numEdges: number;
  teeX: number;
  teeSurfaceY: number;
  holeX: number;
  holeSurfaceY: number;
  holeDepth: number;
  solutionVx: number;
  solutionVy: number;
  fillColor: number;
  /** Solution trajectory points (ball position every 4 steps). */
  trajectory: Vec2[];
  /** Per-connection segment groups for debug visualization. */
  connectionGroups: Segment[][];
  /** Number of generation attempts required. */
  attempts: number;
}

/** A terrain that never collides — used for free-space simulation. */
const nullTerrain: Terrain = {
  collide(_center: Vec2, _radius: number): CollisionResult | null {
    return null;
  },
  verticalWrap: false,
};

const MAX_LAUNCH_SPEED = 1200;
const MAX_RETRIES = 50;
const PAD_WIDTH = 80;
const MIN_CUP_DEPTH = 20;
const MAX_CUP_DEPTH = 35;
const EDGE_MIN_WIDTH = 30;
const EDGE_MAX_WIDTH = 50;
const SIM_MAX_STEPS = 20000;
const HOLE_CAPTURE_SPEED = 20;
const FIRST_HOLE_PROBABILITY = 0.05;
const HOLE_PROBABILITY = 0.15;
const MAX_PLACEMENTS = 6;
const MIN_FLIGHT_STEPS = 100;
const MAX_FLIGHT_STEPS = 400;
const MAX_EXTRA_FLIGHT = 1200;
const VALID_Y_MIN = 40;
const VALID_Y_MAX = GAME_HEIGHT - 40;

/** Simulate ball in free space for a number of steps, then continue until
 *  the ball is in a valid on-screen Y range. Returns null if the ball
 *  never enters valid range within the step budget. */
function simulateFreeToValid(
  startX: number,
  startY: number,
  vx: number,
  vy: number,
  numSteps: number,
): { pos: Vec2; vel: Vec2 } | null {
  const ball = createBall(startX, startY, vx, vy);

  // Simulate the requested number of steps
  for (let i = 0; i < numSteps; i++) {
    step(ball, nullTerrain);
  }

  // If already on-screen, return immediately
  if (ball.pos.y >= VALID_Y_MIN && ball.pos.y <= VALID_Y_MAX) {
    return {
      pos: vec2(ball.pos.x, ball.pos.y),
      vel: vec2(ball.vel.x, ball.vel.y),
    };
  }

  // Ball is off-screen — keep simulating until it enters valid range
  for (let i = 0; i < MAX_EXTRA_FLIGHT; i++) {
    step(ball, nullTerrain);
    if (ball.pos.y >= VALID_Y_MIN && ball.pos.y <= VALID_Y_MAX) {
      return {
        pos: vec2(ball.pos.x, ball.pos.y),
        vel: vec2(ball.vel.x, ball.vel.y),
      };
    }
    // Ball fell below screen — give up
    if (ball.pos.y > GAME_HEIGHT + 200) {
      return null;
    }
  }

  return null;
}

/** Simulate from the tee with all terrain, running for numSteps then
 *  continuing until the ball is in a valid on-screen Y range.
 *  Returns the ball's position and velocity, or null if the ball
 *  falls off-screen or never reaches valid range. */
function simulateFromTeeToValid(
  teeX: number,
  teeY: number,
  vx: number,
  vy: number,
  segments: Segment[],
  numSteps: number,
): { pos: Vec2; vel: Vec2; atRest: boolean } | null {
  const terrain = new SegmentTerrain(segments);
  const ball = createBall(teeX, teeY, vx, vy);

  for (let i = 0; i < numSteps; i++) {
    step(ball, terrain);
    if (ball.atRest) {
      return { pos: vec2(ball.pos.x, ball.pos.y), vel: vec2(0, 0), atRest: true };
    }
    if (ball.pos.y > GAME_HEIGHT + 200) return null;
  }

  // If already valid, return
  if (ball.pos.y >= VALID_Y_MIN && ball.pos.y <= VALID_Y_MAX) {
    return {
      pos: vec2(ball.pos.x, ball.pos.y),
      vel: vec2(ball.vel.x, ball.vel.y),
      atRest: false,
    };
  }

  // Continue until valid Y
  for (let i = 0; i < MAX_EXTRA_FLIGHT; i++) {
    step(ball, terrain);
    if (ball.atRest) {
      return { pos: vec2(ball.pos.x, ball.pos.y), vel: vec2(0, 0), atRest: true };
    }
    if (ball.pos.y >= VALID_Y_MIN && ball.pos.y <= VALID_Y_MAX) {
      return {
        pos: vec2(ball.pos.x, ball.pos.y),
        vel: vec2(ball.vel.x, ball.vel.y),
        atRest: false,
      };
    }
    if (ball.pos.y > GAME_HEIGHT + 200) return null;
  }

  return null;
}

const MIN_SEGMENT_CLEARANCE = 10;

/** Distance from a point to the closest point on a line segment. */
export function pointToSegmentDist(p: Vec2, seg: Segment): number {
  const abx = seg.b.x - seg.a.x;
  const aby = seg.b.y - seg.a.y;
  const apx = p.x - seg.a.x;
  const apy = p.y - seg.a.y;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq === 0) {
    return Math.sqrt(apx * apx + apy * apy);
  }
  const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / abLenSq));
  const cx = seg.a.x + abx * t - p.x;
  const cy = seg.a.y + aby * t - p.y;
  return Math.sqrt(cx * cx + cy * cy);
}

/** Check if two line segments intersect. */
export function segmentsIntersect(s1: Segment, s2: Segment): boolean {
  const d1x = s1.b.x - s1.a.x;
  const d1y = s1.b.y - s1.a.y;
  const d2x = s2.b.x - s2.a.x;
  const d2y = s2.b.y - s2.a.y;
  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-10) return false;
  const dx = s2.a.x - s1.a.x;
  const dy = s2.a.y - s1.a.y;
  const t = (dx * d2y - dy * d2x) / cross;
  const u = (dx * d1y - dy * d1x) / cross;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

/** Minimum distance between two line segments. */
function minSegmentDist(s1: Segment, s2: Segment): number {
  if (segmentsIntersect(s1, s2)) return 0;
  return Math.min(
    pointToSegmentDist(s1.a, s2),
    pointToSegmentDist(s1.b, s2),
    pointToSegmentDist(s2.a, s1),
    pointToSegmentDist(s2.b, s1),
  );
}

/** Create a landscape edge segment at the given position, oriented
 *  perpendicular to the ball's velocity so the ball bounces off the centre.
 *  Returns null if the edge would straddle the wrap boundary or overlap
 *  existing features. */
function placeLandscapeEdge(
  rng: Random,
  ballX: number,
  ballY: number,
  ballVx: number,
  ballVy: number,
  existingSegments: Segment[],
): Segment | null {
  const speed = Math.sqrt(ballVx * ballVx + ballVy * ballVy);
  if (speed < 1) return null; // Ball barely moving

  const width = rng.float(EDGE_MIN_WIDTH, EDGE_MAX_WIDTH);
  const halfW = width / 2;

  // Edge direction is perpendicular to ball velocity
  const edgeDirX = -ballVy / speed;
  const edgeDirY = ballVx / speed;

  // Contact point: ball centre + ball radius in direction of travel
  const contactX = ballX + PHYSICS.BALL_RADIUS * (ballVx / speed);
  const contactY = ballY + PHYSICS.BALL_RADIUS * (ballVy / speed);

  // Edge endpoints centred on the contact point
  const ax = contactX - halfW * edgeDirX;
  const ay = contactY - halfW * edgeDirY;
  const bx = contactX + halfW * edgeDirX;
  const by = contactY + halfW * edgeDirY;

  // Skip if the edge would straddle the wrap boundary or go off-screen
  if (ax < 0 || bx < 0 || ax > GAME_WIDTH || bx > GAME_WIDTH) return null;
  if (ay < 0 || by < 0 || ay > GAME_HEIGHT || by > GAME_HEIGHT) return null;

  const edge: Segment = { a: vec2(ax, ay), b: vec2(bx, by) };

  // Check minimum distance to all existing segments
  for (const seg of existingSegments) {
    if (minSegmentDist(edge, seg) < MIN_SEGMENT_CLEARANCE) {
      return null;
    }
  }

  return edge;
}

/** Create cup segments (floor + 2 walls) at the given position.
 *  Width is constant (HOLE_WIDTH), wall heights are independently random. */
function placeCup(
  rng: Random,
  ballX: number,
  ballY: number,
): { segments: Segment[]; holeX: number; holeSurfaceY: number; holeDepth: number } {
  const leftDepth = rng.float(MIN_CUP_DEPTH, MAX_CUP_DEPTH);
  const rightDepth = rng.float(MIN_CUP_DEPTH, MAX_CUP_DEPTH);
  const cupDepth = Math.max(leftDepth, rightDepth);

  const halfW = HOLE_WIDTH / 2;
  const surfaceY = ballY + PHYSICS.BALL_RADIUS;
  const bottomY = surfaceY + cupDepth;

  const holeX = wrapX(ballX);
  const leftX = holeX - halfW;
  const rightX = holeX + halfW;

  const floor: Segment = { a: vec2(leftX, bottomY), b: vec2(rightX, bottomY) };
  const leftWall: Segment = { a: vec2(leftX, surfaceY + (cupDepth - leftDepth)), b: vec2(leftX, bottomY) };
  const rightWall: Segment = { a: vec2(rightX, surfaceY + (cupDepth - rightDepth)), b: vec2(rightX, bottomY) };

  return {
    segments: [floor, leftWall, rightWall],
    holeX,
    holeSurfaceY: surfaceY,
    holeDepth: cupDepth,
  };
}

interface VerifyResult {
  valid: boolean;
  trajectory: Vec2[];
}

/** Verify that a solution shot reaches the hole and bounces off edges in order.
 *  Segments are ordered: [pad, edge1, edge2, ..., cupFloor, cupLeftWall, cupRightWall].
 *  numEdges is the count of landscape edge segments (between pad and cup).
 *  Returns validity and the recorded trajectory (ball positions every 4 steps). */
function verifySolution(
  segments: Segment[],
  numEdges: number,
  teeX: number,
  teeY: number,
  vx: number,
  vy: number,
  holeX: number,
  holeSurfaceY: number,
  holeDepth: number,
): VerifyResult {
  const terrain = new SegmentTerrain(segments);
  const ball = createBall(teeX, teeY, vx, vy);
  const trajectory: Vec2[] = [];

  const cupStart = 1 + numEdges; // Index of first cup segment
  let lastTouchedEdge = 0; // 0 = no edges touched yet
  let prevCollidedIndex = -1;

  for (let i = 0; i < SIM_MAX_STEPS; i++) {
    step(ball, terrain);
    if (i % 4 === 0) trajectory.push(vec2(ball.pos.x, ball.pos.y));

    const ci = terrain.lastCollidedSegmentIndex;

    // Check bounce order on new collisions (different segment from last step)
    if (ci >= 0 && ci !== prevCollidedIndex) {
      if (ci === 0) {
        // Ball hit the tee pad — fail
        return { valid: false, trajectory };
      } else if (ci >= 1 && ci < cupStart) {
        // Ball hit an edge — must be the next expected one
        if (ci !== lastTouchedEdge + 1) return { valid: false, trajectory };
        lastTouchedEdge = ci;
      }
      // Cup segments are always OK
    }
    prevCollidedIndex = ci;

    const speed = Math.sqrt(lengthSq(ball.vel));
    if (isInHole(ball.pos.x, ball.pos.y, speed, holeX, holeSurfaceY, HOLE_WIDTH, holeDepth, HOLE_CAPTURE_SPEED)) {
      // All placed edges must have been touched
      if (lastTouchedEdge !== numEdges) return { valid: false, trajectory };
      return { valid: true, trajectory };
    }

    if (ball.atRest) return { valid: false, trajectory };
    if (ball.pos.y > GAME_HEIGHT + 200) return { valid: false, trajectory };
  }

  return { valid: false, trajectory };
}

/** Check if a position is valid for cup placement. */
function isValidCupPosition(cupX: number, cupY: number, teeX: number): boolean {
  const wrappedX = wrapX(cupX);
  if (wrappedX < 40 || wrappedX > GAME_WIDTH - 40) return false;
  if (cupY < 60 || cupY > GAME_HEIGHT - 60) return false;
  const dx = Math.abs(wrappedX - teeX);
  const wrappedDist = Math.min(dx, GAME_WIDTH - dx);
  if (wrappedDist < 150) return false;
  return true;
}

/** Try to generate a level with specific tee position and launch parameters.
 *  Returns LevelData on success, or null on failure. */
function tryGenerateWithParams(
  rng: Random,
  teeX: number,
  teeY: number,
  vx: number,
  vy: number,
): LevelData | null {
  const padHalfW = PAD_WIDTH / 2;
  const padSegment: Segment = {
    a: vec2(teeX - padHalfW, teeY),
    b: vec2(teeX + padHalfW, teeY),
  };

  const segments: Segment[] = [padSegment];
  const ballStartY = teeY - TEE_HEIGHT - PHYSICS.BALL_RADIUS;

  let cup: { segments: Segment[]; holeX: number; holeSurfaceY: number; holeDepth: number } | null = null;

  for (let placement = 0; placement < MAX_PLACEMENTS; placement++) {
    let ballPos: Vec2;
    let ballVel: Vec2;
    let ballAtRest = false;

    if (placement === 0) {
      const flightSteps = rng.int(MIN_FLIGHT_STEPS, MAX_FLIGHT_STEPS);
      const flight = simulateFreeToValid(teeX, ballStartY, vx, vy, flightSteps);
      if (!flight) return null;
      ballPos = flight.pos;
      ballVel = flight.vel;
    } else {
      const flightSteps = rng.int(MIN_FLIGHT_STEPS, MAX_FLIGHT_STEPS);
      const totalSteps = flightSteps + placement * 300;
      const sim = simulateFromTeeToValid(teeX, ballStartY, vx, vy, segments, totalSteps);
      if (!sim) return null;
      ballPos = sim.pos;
      ballVel = sim.vel;
      ballAtRest = sim.atRest;
    }

    const holeProbability = placement === 0 ? FIRST_HOLE_PROBABILITY : HOLE_PROBABILITY;
    const tryHole = rng.chance(holeProbability);

    if ((tryHole || ballAtRest) && isValidCupPosition(ballPos.x, ballPos.y, teeX)) {
      cup = placeCup(rng, ballPos.x, ballPos.y);

      let cupClear = true;
      for (const cupSeg of cup.segments) {
        for (const existingSeg of segments) {
          if (minSegmentDist(cupSeg, existingSeg) < MIN_SEGMENT_CLEARANCE) {
            cupClear = false;
            break;
          }
        }
        if (!cupClear) break;
      }

      if (cupClear) {
        const allSegments = [...segments, ...cup.segments];
        const numEdges = segments.length - 1;

        const result = verifySolution(
          allSegments,
          numEdges,
          teeX, ballStartY, vx, vy,
          cup.holeX, cup.holeSurfaceY,
          cup.holeDepth,
        );

        if (result.valid) {
          segments.push(...cup.segments);

          const rngState = rng.getState();
          const debugGroups = connectSegmentsDebug([...segments], numEdges, result.trajectory, rng);
          for (let g = 0; g < debugGroups.length; g++) {
            debugGroups[g] = subdivideConnections(debugGroups[g]!, segments, result.trajectory, rng);
          }

          rng.setState(rngState);
          const connectingSegs = connectSegments([...segments], numEdges, result.trajectory, rng);
          const subdividedSegs = subdivideConnections(connectingSegs, segments, result.trajectory, rng);

          segments.push(...subdividedSegs);

          return {
            segments,
            numEdges,
            teeX,
            teeSurfaceY: teeY,
            holeX: cup.holeX,
            holeSurfaceY: cup.holeSurfaceY,
            holeDepth: cup.holeDepth,
            solutionVx: vx,
            solutionVy: vy,
            fillColor: 0x4a8c3f,
            trajectory: result.trajectory,
            connectionGroups: debugGroups,
            attempts: 0, // set by caller
          };
        }
      }

      cup = null;
      if (ballAtRest) return null;
    }

    if (ballAtRest) return null;

    const edge = placeLandscapeEdge(rng, ballPos.x, ballPos.y, ballVel.x, ballVel.y, segments);
    if (edge) {
      segments.push(edge);
    }
  }

  return null;
}

/** Generate a level from a seed.
 *  If fixedTee is provided, the tee position is locked and only launch params vary. */
export function generateLevel(seed: number, fixedTee?: { x: number; y: number }): LevelData {
  // Phase 1: try different tee positions and launch parameters
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    const currentSeed = seed + attempt * 9999;
    const rng = new Random(currentSeed);

    let teeX: number;
    let teeY: number;
    if (fixedTee) {
      teeX = fixedTee.x;
      teeY = fixedTee.y;
      // Consume the same RNG values so launch params still vary per attempt
      rng.float(0, 1);
      rng.float(0, 1);
    } else {
      const teeXRaw = rng.float(0, 1);
      const teeXBiased = Math.sqrt(teeXRaw);
      teeX = Math.round(20 + teeXBiased * (GAME_WIDTH - 40));
      teeY = Math.round(rng.float(GAME_HEIGHT * 0.40, GAME_HEIGHT * 0.75));
    }

    const teeNorm = teeX / GAME_WIDTH;
    const rightProb = 0.8 - teeNorm * 0.6;
    const launchRight = rng.chance(rightProb);

    const angleDeg = rng.float(25, 65);
    const angleRad = angleDeg * (Math.PI / 180);
    const powerFraction = rng.float(0.35, 0.75);
    const speed = powerFraction * MAX_LAUNCH_SPEED;

    const vx = (launchRight ? 1 : -1) * speed * Math.cos(angleRad);
    const vy = -speed * Math.sin(angleRad);

    const result = tryGenerateWithParams(rng, teeX, teeY, vx, vy);
    if (result) {
      result.attempts = attempt + 1;
      return result;
    }
  }

  // Phase 2: keep the tee position but try different launch params
  let teeX: number;
  let teeY: number;
  if (fixedTee) {
    teeX = fixedTee.x;
    teeY = fixedTee.y;
  } else {
    const baseRng = new Random(seed);
    const teeXRaw = baseRng.float(0, 1);
    const teeXBiased = Math.sqrt(teeXRaw);
    teeX = Math.round(20 + teeXBiased * (GAME_WIDTH - 40));
    teeY = Math.round(baseRng.float(GAME_HEIGHT * 0.40, GAME_HEIGHT * 0.75));
  }
  console.log(`Seed ${seed}: all ${MAX_RETRIES} attempts failed, retrying with fixed tee position...`);

  let retryAttempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    retryAttempt++;
    const retrySeed = seed + (MAX_RETRIES + retryAttempt) * 9999;
    const rng = new Random(retrySeed);

    // Random launch parameters
    const launchRight = rng.chance(0.5);
    const angleDeg = rng.float(25, 65);
    const angleRad = angleDeg * (Math.PI / 180);
    const powerFraction = rng.float(0.35, 0.75);
    const speed = powerFraction * MAX_LAUNCH_SPEED;

    const vx = (launchRight ? 1 : -1) * speed * Math.cos(angleRad);
    const vy = -speed * Math.sin(angleRad);

    const result = tryGenerateWithParams(rng, teeX, teeY, vx, vy);
    if (result) {
      result.attempts = MAX_RETRIES + retryAttempt;
      console.log(`Seed ${seed}: succeeded after ${retryAttempt} additional attempt(s)`);
      return result;
    }

    if (retryAttempt % 100 === 0) {
      console.log(`Seed ${seed}: ${retryAttempt} additional attempts so far...`);
    }
  }
}
